/**
 * "In this Project", docked on the right of the workspace on desktop (design board "Workspace"; it replaces the header
 * avatars' popover there): the people and the AI agents here, each with what they are doing now; Follow a person, see
 * the preview they see, Pause an agent (owner: any, editor: the ones they drive), open an agent's chat. Then the
 * shared preview's thumbnail (collab/SharedPreviewThumbnail: the preview someone else is on, with their pointer) and
 * how you are seen; Follow… and Leave a note, and who it is shared with (Manage opens Share). Collapsible; Canvas
 * remembers it.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'antd';
import { X } from '@phosphor-icons/react';
import { ParticipantMark, listedAgents, openAgentSession, openPreviewFor, sessionLinkLabel } from '../presence/Presence';
import SharedPreviewThumbnail from '../collab/SharedPreviewThumbnail';
import { openMessages } from '../collab/Messages';
import { canMessageAgents, canSendMessages } from '../../utils/messagesModel';
import { suggestionsLabel } from '../../utils/collab3Model';
import { followPerson, getPresencePrivacy, pauseAgent } from '../../utils/presenceClient';
import { getAccess } from '../../services/CollabService';
import { previewOfAgent, previewOfPerson, seeLabel } from '../../utils/collab3Model';
import { inkFor } from '../../utils/participantColors';
import { personDoing, agentDoing } from '../../utils/inProjectModel';
import { agentName, canPause, firstName, followersOf, othersOf, pausedLabel } from '../../utils/presenceModel';
import VendorName from '../sandbox/VendorName';
import './Workspace.css';

// The panel is 280 px wide with 14 px of padding a side: the thumbnail fills it.
const PREVIEW_WIDTH = 248;

/** How many people the project is shared with (its members, the owner left out), or null while unknown. */
function useSharedWith(projectId) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setCount(null);
    getAccess(projectId)
      .then((access) => { if (!cancelled) { setCount((access?.members || []).length); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projectId]);
  return count;
}

export default function InThisProjectPanel({ projectId, snapshot, onClose = null, onLeaveNote = null, onOpenNotes = null, onManageSharing = null, onOpenSuggestions = null, suggestionsPending = 0 }) {
  const navigate = useNavigate();
  const you = snapshot?.you;
  const people = othersOf(snapshot);
  const agents = listedAgents(snapshot);
  const followers = followersOf(snapshot);
  const privacy = getPresencePrivacy();
  const sharedWith = useSharedWith(projectId);
  // You, as the others see you (your name, mark and views come with the people of the snapshot).
  const me = you ? { ...((snapshot?.people || []).find((person) => person.id === you.id) || {}), ...you } : null;
  const hereCount = people.length + agents.length + (you ? 1 : 0);
  // Message: a person (by their sub; Messages finds their address), a viewer too, or an AI agent here (not a viewer:
  // vfs-service refuses their note for the agents). Messages: the chat, for everyone.
  const canMessage = Boolean(projectId) && canSendMessages(you?.role);
  const canMessageAgent = Boolean(projectId) && canMessageAgents(you?.role);
  const messageTo = (recipient, agent = null) => openMessages({ projectId, view: 'compose', recipient, ...(agent ? { agent } : {}), place: null });
  return (
    <aside aria-label="People in this Project" className="in-project" data-testid="in-project-panel">
      <div className="in-project-head">
        <h2>In this Project</h2>
        {onClose && (
          <button aria-label="Hide the In this Project panel" className="in-project-close" onClick={onClose} title="Hide" type="button">
            <X size={14} />
          </button>
        )}
      </div>
      {hereCount > 0 && <div className="in-project-label">Here now · {hereCount}</div>}
      {people.length === 0 && agents.length === 0 && (
        <p className="in-project-empty">Only you here right now. People you share the project with show here when they open it, with their AI agents.</p>
      )}
      {people.map((person) => {
        const following = snapshot?.following === person.id;
        const preview = previewOfPerson(person);
        return (
          <div className="in-project-row" data-testid="in-project-person" key={person.id}>
            <ParticipantMark idle={person.state === 'idle'} initials={person.initials} size={28} slot={person.slot} />
            <div className="in-project-who">
              <div><strong>{person.name}</strong>{person.role ? <span className="in-project-muted"> · {person.role}</span> : null}</div>
              <div className="in-project-muted">{personDoing(person)}</div>
            </div>
            <div className="in-project-actions">
              {preview && (
                <button className="in-project-btn" data-testid="see-preview" onClick={() => openPreviewFor(projectId, preview)} title={`Opens your preview at ${preview.route}`} type="button">
                  {seeLabel(person, you).replace(/^See what (\S+) sees$/, 'See $1’s view')}
                </button>
              )}
              <button aria-pressed={following} className={`in-project-btn${following ? ' is-on' : ''}`} onClick={() => followPerson(projectId, following ? null : person.id)} type="button">
                {following ? 'Following' : 'Follow'}
              </button>
              {canMessage && person.sub && (
                <button aria-label={`Message ${person.name || 'them'}`} className="in-project-btn" data-testid="message-person" onClick={() => messageTo(`sub:${person.sub}`)} type="button">Message</button>
              )}
            </div>
          </div>
        );
      })}
      {agents.map((agent) => (
        <div className={`in-project-row is-agent${agent.paused ? ' is-paused' : ''}`} data-testid="in-project-agent" key={agent.id} style={{ '--presence-ink': inkFor(agent.slot) }}>
          <ParticipantMark agent initials={agent.driver?.initials || 'AI'} paused={Boolean(agent.paused)} size={28} slot={agent.slot} />
          <div className="in-project-who">
            <div><strong><VendorName name={agent.label}>{agent.label || 'AI agent'}</VendorName></strong><span className="in-project-muted"> for {agent.driver ? (you && agent.driver.id === you.id ? 'you' : firstName(agent.driver.name)) : 'someone'}</span></div>
            <div className={agent.paused ? 'collab-paused' : 'in-project-muted'}>{agent.paused ? pausedLabel(agent, you) : agentDoing(agent)}</div>
          </div>
          <div className="in-project-actions">
            {previewOfAgent(agent) && !(agent.paths || []).length && (
              <button className="in-project-btn" onClick={() => openPreviewFor(projectId, previewOfAgent(agent))} type="button">{seeLabel(agent, you)}</button>
            )}
            {!agent.paused && agent.sessionId && (
              <button className="in-project-btn" onClick={() => openAgentSession(agent.sessionId)} title={`${agentName(agent, you)}: its chat`} type="button">
                {sessionLinkLabel(agent, you).replace(/^Open /, '')}
              </button>
            )}
            {canPause(agent, snapshot) && (
              <button className="in-project-btn" onClick={() => pauseAgent(projectId, agent.id, !agent.paused)} type="button">{agent.paused ? 'Resume' : 'Pause'}</button>
            )}
            {canMessageAgent && (
              <button aria-label={`Message ${agentName(agent, you)}`} className="in-project-btn" data-testid="message-agent" onClick={() => messageTo(`agent:${agent.id}`, agent)} type="button">Message</button>
            )}
          </div>
        </div>
      ))}
      {me && (people.length > 0 || agents.length > 0) && (
        <div className="in-project-row" data-testid="in-project-you">
          <ParticipantMark initials={me.initials || '?'} size={28} slot={me.slot ?? 0} />
          <div className="in-project-who">
            <div><strong>{me.name || 'You'}</strong><span className="in-project-muted"> (you){me.role ? ` · ${me.role}` : ''}</span></div>
            <div className="in-project-muted">{personDoing(me)}</div>
          </div>
        </div>
      )}
      {(people.length > 0 || onLeaveNote || onOpenNotes || onOpenSuggestions || projectId) && (
        <div className="in-project-actions is-panel">
          {people.length > 0 && (
            <Dropdown
              menu={{ items: people.map((person) => ({ key: person.id, label: person.name || 'Someone' })), onClick: ({ key }) => followPerson(projectId, key) }}
              trigger={['click']}
            >
              <button className="in-project-btn" type="button">Follow…</button>
            </Dropdown>
          )}
          {projectId && <button className="in-project-btn" data-testid="in-project-messages" onClick={() => openMessages({ projectId, view: 'list' })} title={canMessageAgent ? 'Chat with the people here, and leave notes for the AI agents' : 'Chat with the people here'} type="button">Messages</button>}
          {onLeaveNote && <button className="in-project-btn" onClick={onLeaveNote} title="What you did and what is next, for whoever comes after you" type="button">Leave a handoff note</button>}
          {onOpenNotes && <button className="in-project-btn" data-testid="in-project-notes" onClick={onOpenNotes} title="What was done and what is next, left when someone leaves the project" type="button">Handoff notes</button>}
          {onOpenSuggestions && <button className="in-project-btn" data-testid="in-project-suggestions" onClick={onOpenSuggestions} title="Changes to files proposed with Suggest a change; editors accept or decline them" type="button">{suggestionsLabel(suggestionsPending)}</button>}
        </div>
      )}
      <SharedPreviewThumbnail projectId={projectId} snapshot={snapshot} width={PREVIEW_WIDTH} />
      {followers.length > 0 && (
        <div className="in-project-muted" data-testid="followers">
          {followers.map((p) => firstName(p.name)).join(', ')} {followers.length === 1 ? 'is' : 'are'} following you
        </div>
      )}
      {sharedWith !== null && (
        <div className="in-project-muted in-project-shared" data-testid="shared-with">
          {sharedWith === 0 ? 'Not shared yet' : `Shared with ${sharedWith} ${sharedWith === 1 ? 'person' : 'people'}`}
          {onManageSharing && <> · <button className="collab-link" onClick={onManageSharing} type="button">{sharedWith === 0 ? 'Share' : 'Manage'}</button></>}
        </div>
      )}
      <div className="in-project-foot">
        {privacy.presence === false
          ? 'Others do not see you: your presence is off.'
          : privacy.hideCursor ? 'You are visible, your cursor is not.' : 'You are visible with your cursor.'}
        {' '}
        <button className="collab-link" onClick={() => navigate('/settings#set-collab')} type="button">Privacy</button>
      </div>
    </aside>
  );
}

InThisProjectPanel.propTypes = {
  onClose: PropTypes.func,
  onLeaveNote: PropTypes.func,
  onOpenNotes: PropTypes.func,
  onOpenSuggestions: PropTypes.func,
  suggestionsPending: PropTypes.number,
  onManageSharing: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  snapshot: PropTypes.object,
};
